// src/components/modals/QrPdfModal.jsx
import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { getBlob, get } from '../../helpers/apiHelper'; // Asegúrate de tener un helper para peticiones GET JSON

const sizes = [
    { label: 'Pequeño (150x150)', value: '150' },
    { label: 'Mediano (300x300)', value: '300' },
    { label: 'Grande (450x450)', value: '450' },
];

const QrPdfModal = ({ tiendaId, onClose }) => {
    const [selectedSize, setSelectedSize] = useState(sizes[1].value); // Valor por defecto "Mediano"
    const [selectedDesign, setSelectedDesign] = useState(''); // Se asignará al cargar los diseños
    const [designs, setDesigns] = useState([]); // Lista de diseños obtenida dinámicamente
    const [previewUrl, setPreviewUrl] = useState(null);
    const [loading, setLoading] = useState(false);

    // Cargar diseños disponibles desde el backend al montar el componente
    useEffect(() => {
        const fetchDesigns = async () => {
            try {
                const designsList = await get(`tiendas/pdf/designs`);
                setDesigns(designsList);
                if (designsList && designsList.length > 0) {
                    setSelectedDesign(designsList[0].value);
                }
            } catch (error) {
                Swal.fire('Error', 'No se pudo cargar los diseños disponibles', 'error');
            }
        };

        fetchDesigns();
    }, []);

    // Actualizar la previsualización cada vez que cambie el diseño seleccionado o la tienda
    useEffect(() => {
        if (selectedDesign) {
            const loadPreview = async () => {
                setLoading(true);
                try {
                    const blob = await getBlob(`tiendas/${tiendaId}/pdf?design=${selectedDesign}`);
                    // Revocar la URL anterior si existe para evitar fugas de memoria
                    if (previewUrl) {
                        window.URL.revokeObjectURL(previewUrl);
                    }
                    const url = window.URL.createObjectURL(blob);
                    setPreviewUrl(url);
                } catch (error) {
                    Swal.fire('Error', 'No se pudo generar la previsualización del PDF', 'error');
                } finally {
                    setLoading(false);
                }
            };

            loadPreview();
        }
    }, [selectedDesign, tiendaId]);

    const downloadFile = (blob, filename) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const handleDownloadQR = async () => {
        setLoading(true);
        try {
            const blob = await getBlob(`tiendas/${tiendaId}/qr?size=${selectedSize}`);
            downloadFile(blob, `qr-${tiendaId}-${selectedSize}.png`);
        } catch (error) {
            Swal.fire('Error', 'No se pudo descargar el QR', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPDF = async () => {
        setLoading(true);
        try {
            const blob = await getBlob(`tiendas/${tiendaId}/pdf?design=${selectedDesign}`);
            downloadFile(blob, `tienda-${tiendaId}.pdf`);
        } catch (error) {
            Swal.fire('Error', 'No se pudo descargar el PDF', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed z-50 inset-0 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen p-4 text-center sm:p-0">
                {/* Fondo oscuro */}
                <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={onClose}>
                    <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                </div>

                {/* Contenedor del modal */}
                <div className="bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all w-full max-w-md sm:max-w-lg lg:max-w-2xl mx-auto">
                    <div className="px-6 pt-6 pb-4">
                        {/* Header */}
                        <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                                <svg className="w-6 h-6 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Material Promocional
                            </h3>
                            <button
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                            >
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Contenido */}
                        <div className="mt-6 space-y-6">
                            {/* Sección QR */}
                            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                                <h4 className="text-lg font-semibold text-blue-900 mb-3 flex items-center">
                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V6a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1zm12 0h2a1 1 0 001-1V6a1 1 0 00-1-1h-2a1 1 0 00-1 1v1a1 1 0 001 1zM5 20h2a1 1 0 001-1v-1a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1z" />
                                    </svg>
                                    Código QR
                                </h4>
                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Selecciona el tamaño:
                                        </label>
                                        <select
                                            value={selectedSize}
                                            onChange={(e) => setSelectedSize(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                                        >
                                            {sizes.map((s) => (
                                                <option key={s.value} value={s.value}>
                                                    {s.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <button
                                        onClick={handleDownloadQR}
                                        disabled={loading}
                                        className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {loading ? (
                                            <>
                                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Procesando...
                                            </>
                                        ) : (
                                            <>
                                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                                Descargar QR
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>

                            {/* Sección PDF */}
                            <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                                <h4 className="text-lg font-semibold text-purple-900 mb-3 flex items-center">
                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    Folleto Promocional
                                </h4>
                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Selecciona el diseño:
                                        </label>
                                        <select
                                            value={selectedDesign}
                                            onChange={(e) => setSelectedDesign(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                                        >
                                            {designs.map((d) => (
                                                <option key={d.value} value={d.value}>
                                                    {d.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <button
                                        onClick={handleDownloadPDF}
                                        disabled={loading}
                                        className="w-full bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors duration-200 font-medium flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {loading ? (
                                            <>
                                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Procesando...
                                            </>
                                        ) : (
                                            <>
                                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                                Descargar PDF
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>

                            {/* Previsualización del PDF */}
                            {previewUrl && (
                                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                    <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                        Vista Previa del PDF
                                    </h4>
                                    <div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
                                        <iframe
                                            src={previewUrl}
                                            className="w-full h-64 sm:h-80 lg:h-96"
                                            title="Previsualización PDF"
                                            style={{ border: 'none' }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Botones de acción */}
                        <div className="flex justify-end mt-6 pt-4 border-t border-gray-200">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QrPdfModal;
